import React from 'react'
import type { SolicitudListItem } from '../../types'

interface JobPostingsTableProps {
  data: SolicitudListItem[]
  isLoading: boolean
  error: string | null
  onSort: (field: string, order: 'asc' | 'desc') => void
  onViewCandidates: (solicitudId: string) => void
  onViewRequirement?: (solicitudId: string) => void
  onViewWorkflow?: (solicitudId: string) => void
  onCreateRequirement?: () => void
  onLoadMore?: () => void
  hasMore: boolean
  sortField: string
  sortOrder: 'asc' | 'desc'
}

export const JobPostingsTable: React.FC<JobPostingsTableProps> = ({
  data,
  isLoading,
  error,
  onSort: _onSort,
  onViewCandidates,
  onViewRequirement,
  onViewWorkflow,
  onCreateRequirement,
  onLoadMore,
  hasMore,
  sortField: _sortField,
  sortOrder: _sortOrder,
}) => {
  // Helper function to get position name
  const getPositionName = (solicitud: SolicitudListItem): string => {
    // First check if there's a job definition name
    if (solicitud.job_definition_name) {
      return solicitud.job_definition_name
    }
    // Then check if there's a requirement with puesto_solicitado
    if (solicitud.requerimiento?.puesto_solicitado) {
      return solicitud.requerimiento.puesto_solicitado
    }
    // Fallback to job definition structure
    return (
      solicitud.jd?.estructura?.puesto_solicitado?.puesto ||
      'Posición no especificada'
    )
  }

  // Helper function to get job definition info for display
  const getJobDefinitionInfo = (
    solicitud: SolicitudListItem
  ): string | null => {
    if (solicitud.job_definition_name && solicitud.job_definition_puesto) {
      return `${solicitud.job_definition_name} - ${solicitud.job_definition_puesto}`
    }
    if (solicitud.job_definition_name) {
      return solicitud.job_definition_name
    }
    return null
  }

  // Helper function to get the appropriate action button based on requirement status
  const getActionButton = (solicitud: SolicitudListItem) => {
    // Legacy solicitudes (no requirement) - show "Ver Postulantes"
    if (!solicitud.requerimiento) {
      return (
        <button
          type="button"
          onClick={() => onViewCandidates(solicitud.id_solicitud)}
          className="btn-primary text-sm group-hover:shadow-glow"
        >
          <span>Ver Postulantes</span>
          <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
        </button>
      )
    }

    // Requirement-based solicitudes
    if (solicitud.requirement_status === 'pending') {
      // Show "Ver requerimiento" button for pending approval
      return (
        <button
          type="button"
          onClick={() =>
            onViewRequirement
              ? onViewRequirement(solicitud.id_solicitud)
              : onViewCandidates(solicitud.id_solicitud)
          }
          className="btn-secondary text-sm group-hover:shadow-glow"
        >
          <span>Ver requerimiento</span>
          <i className="fa-solid fa-eye ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
        </button>
      )
    }

    if (solicitud.requirement_status === 'rejected') {
      // No button for rejected requirements
      return (
        <div className="text-sm text-red-600 italic">
          Requerimiento rechazado
        </div>
      )
    }

    // For approved requirements, always show "Ver Postulantes" button
    // Job definition step is no longer part of the workflow
    if (solicitud.requirement_status === 'approved') {
      return (
        <button
          type="button"
          onClick={() => onViewCandidates(solicitud.id_solicitud)}
          className="btn-primary text-sm group-hover:shadow-glow"
        >
          <span>Ver Postulantes</span>
          <i className="fa-solid fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
        </button>
      )
    }

    // Default fallback
    return (
      <div className="text-sm text-secondary-500 italic">
        Estado no reconocido
      </div>
    )
  }

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    } catch {
      return 'Fecha inválida'
    }
  }

  // Helper function to format time
  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
      })
    } catch {
      return '--:--'
    }
  }

  // Helper function to get requirement status badge
  const getRequirementStatusBadge = (solicitud: SolicitudListItem) => {
    // For legacy solicitudes without requirements, show "Completado"
    if (!solicitud.requerimiento) {
      return (
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-blue-100 text-blue-800 border-blue-200">
          <i className="fa-solid fa-check-double mr-1"></i>
          Completado
        </div>
      )
    }

    // For requirement-based solicitudes, show simplified status (no job definition step)
    const statusConfig = {
      not_started: {
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: 'fa-circle',
        text: 'No iniciado',
      },
      pending: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'fa-clock',
        text: 'Pendiente aprobación',
      },
      approved: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: 'fa-check-double',
        text: 'Aprobado',
      },
      rejected: {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: 'fa-times-circle',
        text: 'Rechazado',
      },
    }

    const status = solicitud.requirement_status || 'not_started'
    const config =
      statusConfig[status as keyof typeof statusConfig] ||
      statusConfig.not_started

    return (
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}
      >
        <i className={`fa-solid ${config.icon} mr-1`}></i>
        {config.text}
      </div>
    )
  }

  // Helper function to get workflow status badge (for workflow column)
  const getWorkflowStatusBadge = (status?: string) => {
    const statusConfig = {
      pending: {
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: 'fa-clock',
        text: 'Pendiente',
      },
      approved: {
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: 'fa-check-circle',
        text: 'Aprobado',
      },
      rejected: {
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: 'fa-times-circle',
        text: 'Rechazado',
      },
    }

    // If status is empty, null, or undefined, default to approved
    const effectiveStatus = status && status.trim() ? status : 'approved'
    const config =
      statusConfig[effectiveStatus as keyof typeof statusConfig] ||
      statusConfig.approved

    return (
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.className}`}
      >
        <i className={`fa-solid ${config.icon} mr-1`}></i>
        {config.text}
      </div>
    )
  }

  // Loading state
  if (isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="text-secondary-600">Cargando solicitudes...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center space-y-4 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <i className="fa-solid fa-exclamation-triangle text-red-600 text-2xl"></i>
          </div>
          <p className="text-secondary-600">
            Error al cargar las solicitudes: {error}
          </p>
          <button
            className="btn-secondary text-sm"
            onClick={() => window.location.reload()}
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center text-center animate-fade-in">
          <div className="w-24 h-24 bg-gradient-to-r from-secondary-100 to-secondary-200 rounded-3xl flex items-center justify-center mb-6">
            <i className="fa-solid fa-briefcase text-4xl text-secondary-400"></i>
          </div>
          <h3 className="text-2xl font-bold text-secondary-900 mb-3">
            Aún no tienes solicitudes de vacante
          </h3>
          <p className="text-secondary-600 mb-8 max-w-md">
            Comienza creando tu primera solicitud de vacante. El sistema te
            ayudará a gestionar todo el proceso de selección con inteligencia
            artificial.
          </p>
          <button
            className="btn-primary animate-bounce-subtle"
            onClick={() => (window.location.href = '/new-requirement')}
          >
            <i className="fa-solid fa-plus mr-2"></i>
            <span>Crear primera solicitud</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden">
      <table className="min-w-full">
        <thead className="bg-gradient-to-r from-secondary-50 to-secondary-100 border-b border-secondary-200">
          <tr>
            <th className="px-6 py-4 text-left text-sm font-semibold text-secondary-700">
              Posición
            </th>
            <th className="px-6 py-4 text-center text-sm font-semibold text-secondary-700">
              Fecha
            </th>
            <th className="px-6 py-4 text-center text-sm font-semibold text-secondary-700">
              Flujo de Aprobación
            </th>
            <th className="px-6 py-4 text-center text-sm font-semibold text-secondary-700">
              Postulaciones
            </th>
            <th className="px-6 py-4 text-center text-sm font-semibold text-secondary-700">
              Acciones
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-100">
          {data.map((solicitud, index) => (
            <tr
              key={solicitud.id_solicitud}
              className="hover:bg-secondary-50/50 transition-all duration-200 group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-accent-100 to-accent-200 rounded-xl flex items-center justify-center">
                    <i className="fa-solid fa-briefcase text-accent-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-secondary-900">
                      {getPositionName(solicitud)}
                    </p>
                    {getJobDefinitionInfo(solicitud) && (
                      <p className="text-sm text-blue-600 font-medium">
                        {getJobDefinitionInfo(solicitud)}
                      </p>
                    )}
                    <p className="text-sm text-secondary-500">
                      ID: {solicitud.id_solicitud.slice(-8)}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-col items-center">
                  <span className="text-sm font-medium text-secondary-900">
                    {formatDate(solicitud.fecha_creacion)}
                  </span>
                  <span className="text-xs text-secondary-500">
                    {formatTime(solicitud.fecha_creacion)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                {getWorkflowStatusBadge(solicitud.approval_status)}
              </td>
              <td className="px-6 py-4 text-center">
                <div className="inline-flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {solicitud.total_postulaciones || 0}
                    </span>
                  </div>
                  <span className="text-sm text-secondary-600">candidatos</span>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  {getActionButton(solicitud)}

                  {onViewWorkflow && (
                    <button
                      type="button"
                      onClick={() => onViewWorkflow(solicitud.id_solicitud)}
                      className="btn-secondary text-sm"
                      title="Ver flujo de aprobación"
                    >
                      <i className="fa-solid fa-sitemap"></i>
                      <span className="ml-1">Flujo</span>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Load More Button */}
      {hasMore && onLoadMore && (
        <div className="flex justify-center py-6 border-t border-secondary-200">
          <button
            className="btn-secondary flex items-center space-x-2"
            onClick={onLoadMore}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-secondary-600"></div>
                <span>Cargando...</span>
              </>
            ) : (
              <>
                <i className="fa-solid fa-chevron-down"></i>
                <span>Cargar más</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}
